/**
 * useCloudLibrary — React hook for cloud library CRUD operations.
 * Follows the walletFetch pattern from WalletContext.jsx.
 */
import { API_BASE } from '../config.js';
import { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

// Brightivity: My library on brightivity.dev (/library/designs = the StudioCore /library/designs contract).
const LIBRARY_API = API_BASE;

export default function useCloudLibrary() {
  const { user } = useAuth();
  const [designs, setDesigns] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Authenticated fetch helper.
   */
  const libraryFetch = useCallback(async (endpoint, options = {}) => {
    if (!user?.token) throw new Error('Not authenticated');

    const res = await fetch(`${LIBRARY_API}${endpoint}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${user.token}`,
        ...options.headers,
      },
    });

    const json = await res.json();
    if (!res.ok) {
      throw new Error(json.error || `Request failed (${res.status})`);
    }
    return json.data || json;
  }, [user?.token]);

  /**
   * Fetch designs list with pagination and filters.
   * @param {{ page?: number, limit?: number, source?: string, search?: string, category?: string }} opts
   */
  const fetchDesigns = useCallback(async (opts = {}) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (opts.page) params.set('page', String(opts.page));
      if (opts.limit) params.set('limit', String(opts.limit));
      if (opts.source) params.set('source', opts.source);
      if (opts.search) params.set('search', opts.search);
      if (opts.category) params.set('category', opts.category);

      const qs = params.toString();
      const data = await libraryFetch(`/library/designs${qs ? `?${qs}` : ''}`);
      setDesigns(data.designs || []);
      setTotal(data.total || 0);
      return data;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [libraryFetch]);

  /**
   * Fetch the ENTIRE library (loops pages; the worker caps `limit` at 50).
   * Needed for the desktop-parity left panel: category counts and client-side
   * search/sort/category filtering all operate over the full design set, the
   * same way the desktop app loads everything from local SQLite.
   */
  const fetchAllDesigns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let all = [];
      let page = 1;
      let grandTotal = 0;
      const pageLimit = 50; // worker max (be_cloud_library list.ts)
      // Hard page cap (50 × 100 = 5000 designs) as a runaway guard.
      while (page <= 100) {
        const data = await libraryFetch(`/library/designs?page=${page}&limit=${pageLimit}`);
        const batch = data.designs || [];
        all = all.concat(batch);
        grandTotal = data.total || all.length;
        if (all.length >= grandTotal || batch.length < pageLimit) break;
        page += 1;
      }
      setDesigns(all);
      setTotal(grandTotal);
      return all;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [libraryFetch]);

  /**
   * Save a new design to the cloud library.
   * @param {{ thumbnail: Blob, file?: Blob, designJson?: string, name: string, source: string, imageUrl?: string, metadata?: object, width?: number, height?: number, category?: string }} data
   * @returns {Promise<{ id, name, thumbnailUrl, createdAt } | null>}
   */
  const saveDesign = useCallback(async (data) => {
    if (!user?.token) throw new Error('Not authenticated');

    const formData = new FormData();
    if (data.thumbnail && data.thumbnail.size > 0) {
      formData.append('thumbnail', data.thumbnail, 'thumbnail.png');
    }
    formData.append('name', data.name);
    formData.append('source', data.source);

    if (data.file) formData.append('file', data.file, 'original.png');
    if (data.designJson) formData.append('designJson', data.designJson);
    if (data.imageUrl) formData.append('imageUrl', data.imageUrl);
    if (data.metadata) formData.append('metadata', JSON.stringify(data.metadata));
    if (data.width) formData.append('width', String(data.width));
    if (data.height) formData.append('height', String(data.height));
    if (data.category) formData.append('category', data.category);

    const res = await fetch(`${LIBRARY_API}/library/designs`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${user.token}`,
        // No Content-Type — browser sets multipart boundary automatically
      },
      body: formData,
    });

    const json = await res.json();
    if (!res.ok) {
      throw new Error(json.error || `Upload failed (${res.status})`);
    }
    return json.data || json;
  }, [user?.token]);

  /**
   * Get a single design by ID.
   */
  const getDesign = useCallback(async (id) => {
    return await libraryFetch(`/library/designs/${id}`);
  }, [libraryFetch]);

  /**
   * Update a design's metadata.
   * @param {string} id
   * @param {{ name?: string, category?: string }} updates
   */
  const updateDesign = useCallback(async (id, updates) => {
    const data = await libraryFetch(`/library/designs/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });

    // Update local state
    setDesigns(prev => prev.map(d => d.id === id ? { ...d, ...updates } : d));
    return data;
  }, [libraryFetch]);

  /**
   * The caller's library categories (server-side since 2026-09-01).
   *
   * They live on the server because a design's `category` id is cloud-stored:
   * while the names sat in localStorage, every categorised design looked
   * uncategorised on any other device.
   */
  const fetchCategories = useCallback(async () => {
    const data = await libraryFetch('/library/categories');
    return data?.categories ?? [];
  }, [libraryFetch]);

  /**
   * Replace the whole category list. Whole-list writes keep create / rename /
   * delete / reorder atomic — there is no window where one lands and another
   * doesn't. Last-write-wins across devices.
   * @param {Array<{cat_uuid: string, cat_name: string, cat_color?: string}>} categories
   */
  const saveCategories = useCallback(async (categories) => {
    const data = await libraryFetch('/library/categories', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ categories }),
    });
    return data?.categories ?? categories;
  }, [libraryFetch]);

  /**
   * Delete a design.
   * @param {string} id
   */
  const deleteDesign = useCallback(async (id) => {
    await libraryFetch(`/library/designs/${id}`, { method: 'DELETE' });

    // Remove from local state
    setDesigns(prev => prev.filter(d => d.id !== id));
    setTotal(prev => Math.max(0, prev - 1));
  }, [libraryFetch]);

  return {
    designs,
    total,
    loading,
    error,
    fetchDesigns,
    fetchAllDesigns,
    saveDesign,
    updateDesign,
    deleteDesign,
    getDesign,
    fetchCategories,
    saveCategories,
  };
}
